import React, { useState } from 'react';
import supabase from '../../../supabase/supabase';
import { FiPlay, FiRefreshCw, FiSend } from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const PaymentSimulator = () => {
  const [simulating, setSimulating] = useState(false);
  const [reconResult, setReconResult] = useState(null);
  const [webhookPayload, setWebhookPayload] = useState('{\n  "transaction_reference": "TEST-12345",\n  "status": "SUCCESS",\n  "amount": 1000\n}');

  const runSimulation = async (type) => {
    setSimulating(type);
    try {
      // Simulate internal DB state without hitting external API
      const mockRef = `SIM-${Date.now()}`;
      await supabase.from('payment_logs').insert({
        transaction_reference: mockRef,
        endpoint: '/simulate',
        request_payload: { type },
        response_payload: { status: 'simulated' },
        status_code: 200,
        action_type: 'simulation_run'
      });
      alert(`Simulation completed: ${type}. Check logs for details.`);
    } catch (err) {
      alert(`Simulation failed: ${err.message}`);
    } finally {
      setSimulating(null);
    }
  };

  const handleReconciliation = async () => {
    setSimulating('recon');
    const start = performance.now();
    try {
      const { data, error } = await supabase.functions.invoke('marx-cron-reconcile');
      if (error) throw error;
      setReconResult({ ...data, time: Math.round(performance.now() - start) });
    } catch (err) {
      setReconResult({ error: err.message, time: Math.round(performance.now() - start) });
    } finally {
      setSimulating(null);
    }
  };

  const handleWebhookSubmit = async () => {
    setSimulating('webhook');
    try {
      const payload = JSON.parse(webhookPayload);
      const { data, error } = await supabase.functions.invoke('marx-webhook', { body: payload });
      if (error) throw error;
      alert('Webhook processed successfully. Check logs and transaction explorer.');
    } catch (err) {
      alert(`Webhook error: ${err.message}`);
    } finally {
      setSimulating(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h3 className="text-lg font-serif text-gray-800 mb-4">Payment Simulation</h3>
        <p className="text-xs text-gray-500 mb-4">Safely simulate payment states to verify internal logic without contacting the Marx API.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['Successful Payment', 'Failed Payment', 'Cancelled Payment', 'Timeout'].map(type => (
            <button key={type} onClick={() => runSimulation(type)} disabled={!!simulating} className="p-3 text-xs font-bold uppercase tracking-wider border border-gray-200 rounded-xl hover:border-brand-gold hover:text-brand-gold transition-colors flex items-center justify-center gap-2">
              <SafeIcon icon={FiPlay} /> {type}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-serif text-gray-800">Reconciliation Testing</h3>
          <button onClick={handleReconciliation} disabled={!!simulating} className="bg-brand-darkSage text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors flex items-center gap-2">
            <SafeIcon icon={FiRefreshCw} className={simulating === 'recon' ? 'animate-spin' : ''} /> Run Reconciliation Now
          </button>
        </div>
        {reconResult && (
          <div className="bg-gray-50 p-4 rounded-xl text-sm border border-gray-200">
            {reconResult.error ? (
              <p className="text-red-500">Error: {reconResult.error}</p>
            ) : (
              <div className="flex gap-8">
                <div><span className="text-gray-500 block text-xs">Payments Checked</span><span className="font-bold">{reconResult.reconciled || 0}</span></div>
                <div><span className="text-gray-500 block text-xs">Execution Time</span><span className="font-bold font-mono">{reconResult.time}ms</span></div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h3 className="text-lg font-serif text-gray-800 mb-4">Webhook Testing</h3>
        <textarea 
          value={webhookPayload} 
          onChange={(e) => setWebhookPayload(e.target.value)}
          className="w-full h-32 p-4 text-xs font-mono bg-gray-50 border border-gray-200 rounded-xl focus:border-brand-gold outline-none mb-4"
        />
        <button onClick={handleWebhookSubmit} disabled={!!simulating} className="bg-brand-darkSage text-white px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors flex items-center gap-2">
          <SafeIcon icon={FiSend} /> Submit Sample Webhook
        </button>
      </div>
    </div>
  );
};

export default PaymentSimulator;